import { useLazyQuery, useMutation } from '@apollo/client';
import { ORDER_STATUS } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '../../lib/base';
import { CUSTOMERS_QUERY } from '../../queries/CustomersQuery';
import type {
  EditRecurringOrderCustomerDataQuery,
  EditRecurringOrderCustomerDataQueryVariables,
  EditRecurringOrderCustomerMutation,
  EditRecurringOrderCustomerMutationVariables,
} from '../../types/__generated__/graphql';
import { createID, createOnBlurRequired } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { ChildOrdersContainer } from './ChildOrdersContainer';

const EDIT_REC_ORDER_CUSTOMER_MUT = gql`
  mutation EditRecurringOrderCustomer(
    $recurring_order_id: Int!
    $customer: String!
    $modify_in_progress_orders: Boolean!
  ) {
    editRecurringOrderCustomer(
      input: {
        recurring_order_id: $recurring_order_id
        customer: $customer
        modify_in_progress_orders: $modify_in_progress_orders
      }
    ) {
      updated_recurring_order {
        id
        customer
        updated_at
      }
      updated_orders {
        id
        customer
        updated_at
      }
    }
  }
`;
const EDIT_REC_ORDER_CUSTOMER_DATA = gql`
  query EditRecurringOrderCustomerData($id: ID!) {
    recurring_order(id: $id) {
      id
      customer
      child_orders(
        filters: [
          { name: "order.order_status", operator: "in", value: "planned,in_progress" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            customer
            order_fulfillment_time
            order_status
          }
        }
      }
    }
  }
`;

interface Props {
  readonly recurringOrderId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditRecurringOrderCustomerDialog({ recurringOrderId, isOpen, onClose, onSuccess }: Props) {
  const [editRecurringOrderCustomer, { loading: editSingleOrderLoading }] = useMutation<
    EditRecurringOrderCustomerMutation,
    EditRecurringOrderCustomerMutationVariables
  >(EDIT_REC_ORDER_CUSTOMER_MUT);
  const [getRecurringOrderData, { data: orderData, loading: orderDataLoading, error: orderDataError }] = useLazyQuery<
    EditRecurringOrderCustomerDataQuery,
    EditRecurringOrderCustomerDataQueryVariables
  >(EDIT_REC_ORDER_CUSTOMER_DATA, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringOrderData({
        variables: { id: createID.recurringOrder(recurringOrderId) },
        onCompleted: (orderDataIn) => {
          if (orderDataIn?.recurring_order != null) {
            setCustomerName(orderDataIn.recurring_order.customer ?? '');
            setExistingCustomerName(orderDataIn.recurring_order.customer ?? '');
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, recurringOrderId, getRecurringOrderData]);

  const disableFields = editSingleOrderLoading || orderDataLoading;

  const onCloseInternal = () => {
    onClose();
  };

  const onSubmit = () => {
    void editRecurringOrderCustomer({
      refetchQueries: [{ query: CUSTOMERS_QUERY }],
      variables: {
        recurring_order_id: recurringOrderId,
        customer: customerName,
        modify_in_progress_orders: shouldUpdateInProgressOrders,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editRecurringOrderCustomer != null) {
          enqueueSnackbar('Updated recurring order customer', { variant: 'success' });
          onSuccess(false);
        } else {
          enqueueSnackbar('Error editing recurring order', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const [customerName, setCustomerName] = React.useState('');
  const [customerNameValidation, setCustomerNameValidation] = React.useState('');
  const onCustomerNameBlur = createOnBlurRequired('Customer name is required', setCustomerNameValidation);
  const [existingCustomerName, setExistingCustomerName] = React.useState('');
  const onChangeCustomerName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value !== '') {
      setCustomerNameValidation('');
    }
    setCustomerName(e.target.value);
  };
  const [shouldUpdateInProgressOrders, setShouldUpdateInProgressOrders] = React.useState(false);
  const onChangeShouldUpdateInProgressOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldUpdateInProgressOrders(e.target.checked);
  };

  const hasInputValidationError = customerNameValidation !== '';
  const requiredFieldsFilled: boolean = customerName !== '';
  const somethingChanged = existingCustomerName !== customerName || shouldUpdateInProgressOrders;
  const isSubmitDisabled: boolean =
    !requiredFieldsFilled ||
    hasInputValidationError ||
    disableFields ||
    !somethingChanged ||
    editSingleOrderLoading ||
    orderDataLoading;
  const onEnter = () => {
    if (!isSubmitDisabled && !disableFields) {
      onSubmit();
    }
  };

  const childOrders = orderData?.recurring_order?.child_orders?.edges.map((edge) => edge.node) ?? [];
  const inProgressChildOrders = childOrders.filter((o) => o.order_status === ORDER_STATUS.IN_PROGRESS);
  const plannedChildOrders = childOrders.filter((o) => o.order_status === ORDER_STATUS.PLANNED);

  const dialogContent = orderDataError ? (
    <ErrorView error={orderDataError} />
  ) : orderDataLoading || orderData?.recurring_order == null ? (
    <PageLoading />
  ) : (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="customer-name"
        id="customer-name"
        label="Customer Name"
        value={customerName}
        onChange={onChangeCustomerName}
        error={customerNameValidation !== ''}
        subtext={customerNameValidation}
        onBlur={onCustomerNameBlur}
        // onEnter={onEnter}
        hasSubtext
        required
        disabled={disableFields}
      />
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
        <FormControlLabel
          label="Update In Progress Child Orders"
          control={<Checkbox checked={shouldUpdateInProgressOrders} onChange={onChangeShouldUpdateInProgressOrders} />}
        />
      </Box>
      <Box>
        <Collapse
          unmountOnExit
          in={shouldUpdateInProgressOrders}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <ChildOrdersContainer
            title={
              inProgressChildOrders.length > 0
                ? 'The following in progress child orders will also be updated:'
                : 'There are no in progress child orders to update.'
            }
            ordersIn={inProgressChildOrders}
          />
        </Collapse>
        <ChildOrdersContainer
          title={
            plannedChildOrders.length > 0
              ? 'The following planned child orders will also be updated:'
              : 'There are no planned child orders to update.'
          }
          ordersIn={plannedChildOrders}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseInternal();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Recurring Order Customer Name</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onCloseInternal} />
        </Box>
      </DialogTitle>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onEnter();
          }
        }}
        sx={{ padding: 1 }}
      >
        {dialogContent}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onCloseInternal}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter();
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
